'use client';

/**
 * NotificationBell Component
 *
 * Apple Liquid Glass-inspired notification bell with animated badge.
 * Renders as a GlassIconButton with a pulsing unread count badge.
 * Designed for reuse across all portals (business, consumer, admin).
 *
 * @see Issue #699 - Notification System Fidelity
 * @see Apple WWDC 2025 - Liquid Glass design language
 */

import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { AnimatePresence, motion } from 'framer-motion';
import React, { forwardRef, useCallback, useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { baseColors } from '../../theme/palette';
import { springPresets } from '../../theme/transitions';
import { GlassIconButton } from '../GlassIconButton';

// ============================================================================
// Types
// ============================================================================

export interface NotificationBellProps {
  /** Number of unread notifications */
  unreadCount?: number;

  /** Maximum count to display before showing "99+" */
  maxCount?: number;

  /** Called when the bell is clicked */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;

  /** Whether the dropdown is currently open */
  isOpen?: boolean;

  /** Button size variant */
  size?: 'small' | 'medium' | 'large';

  /** Whether to animate when new notifications arrive */
  animateOnNew?: boolean;

  /** Disable the button */
  disabled?: boolean;
}

// ============================================================================
// Animations
// ============================================================================

// ============================================================================
// Styled Components
// ============================================================================

interface StyledBadgeProps {
  $isDark: boolean;
  $hasUnread: boolean;
}

const StyledBadge = styled(Badge, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledBadgeProps>(({ $isDark, $hasUnread }) => ({
  '& .MuiBadge-badge': {
    // shell.jsx (line 189) renders the unread indicator as a tiny amber dot
    // with a soft glow — `width: 5, height: 5, background: F.amber,
    // boxShadow: 0 0 4px F.amber`. Reused here for the platform's count
    // badge so the topbar's visual rhythm matches the design.
    background: $hasUnread
      ? `linear-gradient(135deg, ${baseColors.warning[400]}, ${baseColors.warning[500]})`
      : 'transparent',
    color: '#fff',
    fontSize: '0.625rem',
    fontWeight: 700,
    minWidth: 14,
    height: 14,
    padding: '0 3px',
    borderRadius: 7,
    // Glass border
    border: $hasUnread ? `1px solid ${$isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)'}` : 'none',
    // Amber glow (shell.jsx: `boxShadow: 0 0 4px ${F.amber}`)
    boxShadow: $hasUnread ? `0 0 4px ${alpha(baseColors.warning[500], 0.9)}` : 'none',
    // Smooth transitions
    transition: 'all 0.3s cubic-bezier(0.215, 0.61, 0.355, 1)',
    // Position fine-tuning — pulled in slightly to sit on the bell silhouette
    top: 2,
    right: 2,
  },
}));

// ============================================================================
// Component
// ============================================================================

export const NotificationBell = forwardRef<HTMLButtonElement, NotificationBellProps>(
  (
    { unreadCount = 0, maxCount = 99, onClick, isOpen = false, size = 'medium', animateOnNew = true, disabled = false },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { t } = useI18n();
    const hasUnread = unreadCount > 0;

    const tooltipText = useMemo(() => {
      if (hasUnread) {
        return t('notification.bell.countLabel', { count: String(Math.min(unreadCount, maxCount)) });
      }
      return t('notification.bell.tooltip');
    }, [hasUnread, unreadCount, maxCount, t]);

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);
      },
      [onClick],
    );

    return (
      <AnimatePresence mode="wait">
        <motion.span
          key={hasUnread ? 'has-unread' : 'no-unread'}
          initial={animateOnNew ? { scale: 0.8, opacity: 0.5 } : false}
          animate={{ scale: 1, opacity: 1 }}
          transition={springPresets.bouncy}
          style={{ display: 'inline-flex' }}
        >
          <GlassIconButton
            ref={ref}
            size={size}
            variant={isOpen ? 'glass' : 'subtle'}
            tooltip={tooltipText}
            onClick={handleClick}
            disabled={disabled}
            color="primary"
            aria-label={tooltipText}
            aria-haspopup="true"
            aria-expanded={isOpen}
            sx={{
              overflow: 'visible',
              color: isOpen
                ? 'primary.main'
                : hasUnread
                  ? isDark
                    ? 'text.primary'
                    : 'text.secondary'
                  : 'text.secondary',
            }}
          >
            <StyledBadge
              badgeContent={hasUnread ? Math.min(unreadCount, maxCount) : 0}
              $isDark={isDark}
              $hasUnread={hasUnread}
              max={maxCount}
            >
              <NotificationsIcon fontSize="small" />
            </StyledBadge>
          </GlassIconButton>
        </motion.span>
      </AnimatePresence>
    );
  },
);

NotificationBell.displayName = 'NotificationBell';

export default NotificationBell;
